import * as React from 'react';
import type { Locale } from '@ministry/ui/internal';

const STORAGE_KEY = 'ms.locale';
const LOCALES: readonly Locale[] = ['en', 'ne'];

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Locale, persisted across reloads.
 *
 * `LocaleProvider` deliberately keeps no storage of its own — it is a design-system
 * primitive, and the gallery mounts it too. Persistence is an application concern, so
 * the app drives the provider through its controlled `locale`/`onLocaleChange` props.
 *
 * Without this, a Nepali reader's choice survives exactly until the next page load,
 * which on a government portal is not a preference toggle — it is a language the user
 * has to re-select on every navigation that touches the server.
 *
 * Reads are defensive: `localStorage` throws outright in some privacy modes, and the
 * stored value is attacker-adjacent only in the sense that anything in a shared browser
 * profile is — an unrecognised value falls back to `en` rather than reaching the
 * dictionary lookup as an arbitrary string.
 */
export function useStoredLocale(): { locale: Locale; setLocale: (next: Locale) => void } {
  const [locale, setLocaleState] = React.useState<Locale>(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return isLocale(stored) ? stored : 'en';
    } catch {
      return 'en';
    }
  });

  const setLocale = React.useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A blocked or full store must never break the toggle; the choice simply
      // does not outlive this tab.
    }
  }, []);

  return { locale, setLocale };
}
